import { IonIcon, IonModal } from "@ionic/react";
import { call, checkmark, clipboardOutline, copyOutline, mailOutline, notificationsOutline } from "ionicons/icons";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useToast from "../../hooks/useToast";
import { SuiviMemberDetail, SuiviSequenceBase } from "../../services/suivi.service";
import dayjs from "dayjs";
import {
  cardElevation,
  DueChip,
  ListCard,
  QuietTag,
  Row,
  RowMain,
  RowSub,
  RowTitle,
  SectionHeader,
  StatusText,
} from "./suiviUi";

type Props = {
  member: SuiviMemberDetail | null;
  /** Ordinal-aware role label (« Enfant 2 ») computed by the group space. */
  roleLabel?: string;
  /** The member's own pending questionnaires — the call script. */
  pending: SuiviSequenceBase[];
  onClose: () => void;
  /** POSTs the server-side relance stamp (shared with site colleagues). */
  onMarkReminded: () => Promise<void>;
};

/**
 * Fiche d'appel — the phone call as a unit of work: the number front and center
 * (tel: on mobile, selectable text + copy for locked hospital desktops), the
 * member's pending questionnaires as the call script, and the shared
 * "relancé" stamp. Bottom sheet chrome comes from theme/modal.scss
 * (.resonance-sheetmodal) — no ModalCard in sheets. The identity band plays the
 * part of the primary toolbar other sheets open on (the drag handle is tinted
 * for it).
 */
const CallSheet: FC<Props> = ({ member, roleLabel, pending, onClose, onMarkReminded }) => {
  const { t } = useTranslation();
  const present = useToast();
  const [stamping, setStamping] = useState(false);

  const stampedToday = member?.last_reminder_stamp
    ? dayjs(member.last_reminder_stamp.at).isSame(dayjs(), "day")
    : false;

  const copyNumber = async (): Promise<void> => {
    if (!member?.phone_number) return;
    try {
      await navigator.clipboard.writeText(member.phone_number);
      present(t("suivi_copied"));
    } catch {
      // Clipboard can be blocked on locked-down browsers; the number stays
      // selectable text as the fallback.
    }
  };

  const copyEmail = async (): Promise<void> => {
    if (!member?.email) return;
    try {
      await navigator.clipboard.writeText(member.email);
      present(t("suivi_email_copied"));
    } catch {
      // Same fallback as the number: the address stays selectable text.
    }
  };

  const markReminded = async (): Promise<void> => {
    setStamping(true);
    try {
      await onMarkReminded();
    } catch {
      present(t("common_server_error_title"));
    } finally {
      setStamping(false);
    }
  };

  const sheetRole = roleLabel ?? member?.role_label ?? null;
  const name = member
    ? [member.firstname, member.lastname].filter(Boolean).join(" ") ||
      sheetRole ||
      t("suivi_member_generic")
    : "";

  return (
    <IonModal
      className="resonance-sheetmodal"
      isOpen={member !== null}
      backdropDismiss={true}
      // onDidDismiss (not onWillDismiss): nulling the member at animation START
      // would blank the sheet content while it slides down.
      onDidDismiss={onClose}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      canDismiss={true}
    >
      {member && (
        <Sheet>
          <IdentityBand>
            {/* Same initial as the member's avatar in the dossier. */}
            <BandAvatar aria-hidden="true">
              {(member.role_label ?? "?").charAt(0).toUpperCase()}
            </BandAvatar>
            <Name>{name}</Name>
            {/* Role line only when it adds information (unnamed member: the title
                already IS the ordinal role label — no duplicate line). */}
            {sheetRole && sheetRole !== name && <Role>{sheetRole}</Role>}
          </IdentityBand>

          <SheetBody>
            {(member.phone_number || member.email) && (
              <ContactCard>
                {member.phone_number && (
                  <ContactBlock>
                    <PhoneNumber>{member.phone_number}</PhoneNumber>
                    <CallLink href={`tel:${member.phone_number}`}>
                      <IonIcon icon={call} aria-hidden="true" />
                      {t("suivi_call")}
                    </CallLink>
                    <CopyButton type="button" onClick={copyNumber}>
                      <IonIcon icon={copyOutline} aria-hidden="true" />
                      {t("suivi_copy_number")}
                    </CopyButton>
                  </ContactBlock>
                )}

                {/* Email — same role-config gating as the phone; mailto on tap,
                    selectable + copy for locked-down desktops. */}
                {member.email && (
                  <ContactBlock>
                    <EmailLine>
                      <IonIcon icon={mailOutline} aria-hidden="true" />
                      <EmailLink href={`mailto:${member.email}`}>{member.email}</EmailLink>
                    </EmailLine>
                    <CopyButton type="button" onClick={copyEmail}>
                      <IonIcon icon={copyOutline} aria-hidden="true" />
                      {t("suivi_copy_email")}
                    </CopyButton>
                  </ContactBlock>
                )}
              </ContactCard>
            )}

            {pending.length > 0 && (
              <>
                <SectionHeader title={t("suivi_during_call")} icon={clipboardOutline} />
                <ListCard>
                  {pending.map((row) => (
                    <Row key={row.user_sequence_id}>
                      <RowMain>
                        <RowTitle>{row.label ?? t("suivi_questionnaire")}</RowTitle>
                        <RowSub>
                          <StatusText $active={row.status === "IN_PROGRESS"}>
                            {row.status === "IN_PROGRESS"
                              ? t("suivi_status_in_progress", {
                                  answered: row.progress.answered,
                                  total: row.progress.total,
                                })
                              : t("suivi_status_not_started")}
                          </StatusText>
                          <DueChip endDate={row.end_date} t={t} />
                          {row.avg_time_to_answer != null && row.avg_time_to_answer > 0 && (
                            <span>{t("suivi_minutes", { count: row.avg_time_to_answer })}</span>
                          )}
                        </RowSub>
                      </RowMain>
                    </Row>
                  ))}
                </ListCard>
              </>
            )}

            <StampArea>
              {stampedToday ? (
                <QuietTag $tone="success">
                  <IonIcon icon={checkmark} aria-hidden="true" />
                  {t("suivi_reminded_on", {
                    date: dayjs(member.last_reminder_stamp!.at).format("DD/MM"),
                  })}
                </QuietTag>
              ) : (
                <StampButton type="button" disabled={stamping} onClick={markReminded}>
                  <IonIcon icon={notificationsOutline} aria-hidden="true" />
                  {t("suivi_mark_reminded")}
                </StampButton>
              )}
            </StampArea>
          </SheetBody>
        </Sheet>
      )}
    </IonModal>
  );
};

/* Full-bleed: cancels the global 1rem padding every modal page gets (theme/
   modal.scss `ion-modal .ion-page`), so the identity band reaches the sheet's
   edges and top — where the drag handle sits. */
const Sheet = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: -1rem;
  overflow-y: auto;
  background: var(--app-color-background, #ffffff);
`;

/* Primary band = the toolbar of the app's other sheets: the drag handle (primary-
   contrast ink, theme/modal.scss) is only visible on it. */
const IdentityBand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 20px 20px;
  text-align: center;
  background:
    radial-gradient(120% 140% at 50% 0%, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0) 60%),
    var(--app-color-primary, #2563eb);
  color: var(--app-color-primary-contrast, #ffffff);
`;

const BandAvatar = styled.span`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  font-family: var(--app-font-heading, inherit);
  font-size: 22px;
  font-weight: 700;
  background: rgba(var(--app-color-primary-contrast-rgb, 255, 255, 255), 0.16);
  box-shadow: inset 0 0 0 1.5px rgba(var(--app-color-primary-contrast-rgb, 255, 255, 255), 0.35);
`;

const Name = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.25;
  color: inherit;
`;

const Role = styled.p`
  margin: 3px 0 0;
  font-size: 13.5px;
  color: rgba(var(--app-color-primary-contrast-rgb, 255, 255, 255), 0.78);
`;

const SheetBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px 26px;
`;

const ContactCard = styled.div`
  ${cardElevation}
  overflow: hidden;
`;

const ContactBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 18px 18px 16px;

  & + & {
    border-top: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.07);
  }
`;

/* Selectable on purpose — hospital desktops without tel: support. */
const PhoneNumber = styled.p`
  margin: 0 0 2px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.05em;
  font-variant-numeric: tabular-nums;
  text-align: center;
  user-select: all;
  color: var(--app-color-text, #0f172a);
`;

const CallLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  align-self: stretch;
  min-height: 48px;
  border-radius: 999px;
  background: var(--app-color-primary, #2563eb);
  color: var(--app-color-primary-contrast, #ffffff);
  font-size: 15.5px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 20px -12px rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.9);
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.1s ease;

  ion-icon {
    font-size: 18px;
  }

  &:active {
    opacity: 0.85;
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.35);
  }
`;

const EmailLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 100%;
  color: var(--app-color-primary, #2563eb);

  ion-icon {
    flex: none;
    font-size: 18px;
  }
`;

/* Selectable + tappable: mailto on tap, text-select for copy on desktops. */
const EmailLink = styled.a`
  font-size: 15px;
  font-weight: 600;
  word-break: break-all;
  user-select: all;
  color: inherit;
  text-decoration: none;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 999px;
  min-height: 36px;
  padding: 0 16px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  background: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.055);
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.75);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  ion-icon {
    font-size: 15px;
  }

  &:active {
    background: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.1);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.35);
  }
`;

const StampArea = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2px;
`;

const StampButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  border: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.14);
  border-radius: 14px;
  min-height: 50px;
  font-family: inherit;
  font-size: 14.5px;
  font-weight: 600;
  background: var(--app-color-surface-tint, #ffffff);
  color: var(--app-color-text, #0f172a);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  ion-icon {
    font-size: 18px;
    color: var(--app-color-primary, #2563eb);
  }

  &:active {
    background: rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.06);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.35);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export default CallSheet;
